import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCalendar, FiMapPin, FiClock, FiTag, FiArrowRight } from 'react-icons/fi';

interface FeaturedEventCardProps {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  category: string;
  imageUrl?: string;
  index: number;
}

export const FeaturedEventCard: React.FC<FeaturedEventCardProps> = ({ 
  id, 
  title, 
  description = '',
  date, 
  time, 
  location, 
  category, 
  imageUrl,
  index
}) => {
  const [imgError, setImgError] = React.useState(false);
  
  // Truncate description if it's too long and exists
  const truncatedDescription = description && description.length > 120 
    ? description.substring(0, 120) + '...' 
    : description || '';
  
  // Default image if none provided
  const backgroundImage = !imgError && imageUrl ? imageUrl : '/placeholder-event.jpg';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -5, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
      className="rounded-xl overflow-hidden bg-white shadow-lg"
    >
      <div className="relative">
        <img 
          src={backgroundImage} 
          alt={title} 
          className="h-48 w-full object-cover"
          onError={() => setImgError(true)}
        />
        <div className="absolute top-0 right-0 m-2">
          <span className="inline-block bg-indigo-500 text-white text-xs px-3 py-1 rounded-full uppercase font-semibold tracking-wide">
            {category || 'Uncategorized'}
          </span>
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="font-bold text-xl mb-2 text-gray-800">{title || 'Untitled Event'}</h3>
        <p className="text-gray-600 text-sm mb-4">{truncatedDescription}</p>
        
        <div className="flex flex-col space-y-2 text-sm text-gray-500 mb-4">
          <div className="flex items-center">
            <FiCalendar className="mr-2 text-indigo-500" />
            <span>{date || 'Date TBA'}</span>
          </div>
          <div className="flex items-center">
            <FiClock className="mr-2 text-indigo-500" />
            <span>{time || 'Time TBA'}</span>
          </div>
          <div className="flex items-center">
            <FiMapPin className="mr-2 text-indigo-500" />
            <span className="truncate max-w-xs">{location || 'Location TBA'}</span>
          </div>
        </div>
        
        <Link 
          to={`/events/${id}`}
          className="inline-flex items-center mt-2 text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
        >
          <span className="mr-1">View details</span>
          <motion.span 
            whileHover={{ x: 3 }}
            whileTap={{ x: 0 }}
          >
            <FiArrowRight />
          </motion.span>
        </Link>
      </div>
    </motion.div>
  );
}; 